import { useState, useRef } from "react";
import { Upload, FileSpreadsheet, CheckCircle, AlertCircle, Download, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { uploadFile } from "../lib/api";
import { toast } from "sonner";

const dataTypes = [
  {
    value: "bank-accounts",
    label: "Bank Accounts",
    columns: "name, bank_name, account_number, balance, account_type",
    sample: `name,bank_name,account_number,balance,account_type
Business Account,HDFC Bank,1234567890,500000,current
Savings Account,ICICI Bank,0987654321,250000,savings`,
  },
  {
    value: "invoices",
    label: "Invoices",
    columns: "invoice_number, client_name, description, base_amount, cgst_rate, sgst_rate, igst_rate, status, invoice_date, due_date",
    sample: `invoice_number,client_name,description,base_amount,cgst_rate,sgst_rate,igst_rate,status,invoice_date,due_date
INV-001,ABC Corp,Web Development,100000,9,9,0,pending,2025-01-01,2025-01-31
INV-002,XYZ Ltd,Consulting,50000,0,0,18,paid,2025-01-10,2025-02-10`,
  },
  {
    value: "payables",
    label: "Payables",
    columns: "vendor_name, description, base_amount, cgst_rate, sgst_rate, igst_rate, status, due_date",
    sample: `vendor_name,description,base_amount,cgst_rate,sgst_rate,igst_rate,status,due_date
Office Supplies Co,Monthly supplies,15000,9,9,0,pending,2025-02-01
Cloud Services,AWS hosting,25000,0,0,18,pending,2025-02-15`,
  },
  {
    value: "fixed-expenses",
    label: "Fixed Expenses",
    columns: "name, category, amount, frequency, employee_name, description",
    sample: `name,category,amount,frequency,employee_name,description
John's Salary,salary,75000,monthly,John Smith,Senior Developer
Office Rent,rent,50000,monthly,,Main office space
Electricity Bill,electricity,8000,monthly,,`,
  },
  {
    value: "onetime-expenses",
    label: "One-time Expenses",
    columns: "name, category, amount, expense_date, description",
    sample: `name,category,amount,expense_date,description
MacBook Pro,equipment,150000,2025-01-05,Developer laptop
Zoom License,software,15000,2025-01-10,Annual subscription`,
  },
];

export default function DataUpload() {
  const { canEdit } = useAuth();
  const [selectedType, setSelectedType] = useState("bank-accounts");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const selectedDataType = dataTypes.find((dt) => dt.value === selectedType);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const ext = selectedFile.name.split(".").pop().toLowerCase();
      if (!["csv", "xlsx", "xls"].includes(ext)) {
        toast.error("Please upload a CSV or Excel file");
        return;
      }
      setFile(selectedFile);
      setResult(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files?.[0];
    if (droppedFile) {
      const ext = droppedFile.name.split(".").pop().toLowerCase();
      if (!["csv", "xlsx", "xls"].includes(ext)) {
        toast.error("Please upload a CSV or Excel file");
        return;
      }
      setFile(droppedFile);
      setResult(null);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error("Please select a file first");
      return;
    }

    setUploading(true);
    try {
      const response = await uploadFile(selectedType, file);
      setResult({
        success: true,
        message: response.data.message,
        count: response.data.count,
      });
      toast.success(response.data.message);
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Upload failed";
      setResult({
        success: false,
        message: errorMsg,
      });
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([selectedDataType.sample], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedType}-template.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const clearFile = () => {
    setFile(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6 animate-fade-in max-w-3xl mx-auto" data-testid="upload-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Upload Data</h1>
          <p className="text-sm text-slate-500 mt-1">
            Import data from CSV or Excel files
          </p>
        </div>
      </div>

      {!canEdit ? (
        <Card>
          <CardContent className="py-12">
            <div className="flex flex-col items-center text-center">
              <Upload className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-base font-semibold text-slate-700">View Only Access</p>
              <p className="text-sm text-slate-500 mt-1">You don't have permission to upload data. Contact the admin for full access.</p>
            </div>
          </CardContent>
        </Card>
      ) : (
      <>
      {/* Data Type Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Select Data Type</CardTitle>
          <CardDescription>
            Choose what type of data you want to import
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Select value={selectedType} onValueChange={setSelectedType}>
            <SelectTrigger data-testid="select-data-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dataTypes.map((dt) => (
                <SelectItem key={dt.value} value={dt.value}>
                  {dt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="mt-4 p-4 bg-slate-50 rounded-lg">
            <p className="text-sm font-medium text-slate-700 mb-2">Required Columns:</p>
            <p className="text-sm text-slate-600 font-mono break-all">
              {selectedDataType?.columns}
            </p>
          </div>

          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            onClick={downloadTemplate}
            data-testid="download-template"
          >
            <Download className="w-4 h-4 mr-2" />
            Download Template CSV
          </Button>
        </CardContent>
      </Card>

      {/* Upload Area */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload File</CardTitle>
          <CardDescription>
            Drag and drop or click to select a CSV/Excel file
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              file ? "border-brand-orange bg-brand-orange/10" : "border-slate-200 hover:border-brand-orange/50"
            }`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            data-testid="upload-dropzone"
          >
            {file ? (
              <div className="flex items-center justify-center gap-3">
                <FileSpreadsheet className="w-10 h-10 text-brand-orange" />
                <div className="text-left">
                  <p className="font-medium text-slate-900">{file.name}</p>
                  <p className="text-sm text-slate-500">
                    {(file.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="ml-2"
                  onClick={clearFile}
                  data-testid="clear-file"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <>
                <Upload className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-600 mb-2">
                  Drag and drop your file here, or
                </p>
                <Button
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="browse-files"
                >
                  Browse Files
                </Button>
                <p className="text-xs text-slate-400 mt-2">
                  Supports CSV, XLSX, XLS
                </p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={handleFileChange}
              className="hidden"
              data-testid="file-input"
            />
          </div>

          {file && (
            <div className="mt-4 flex justify-end">
              <Button
                onClick={handleUpload}
                disabled={uploading}
                className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
                data-testid="upload-btn"
              >
                {uploading ? (
                  <>
                    <span className="animate-spin mr-2">⏳</span>
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Upload & Import
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Result Message */}
          {result && (
            <div
              className={`mt-4 p-4 rounded-lg flex items-start gap-3 ${
                result.success
                  ? "bg-emerald-50 text-emerald-800"
                  : "bg-red-50 text-red-800"
              }`}
              data-testid="upload-result"
            >
              {result.success ? (
                <CheckCircle className="w-5 h-5 text-emerald-500 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-500 mt-0.5" />
              )}
              <div>
                <p className="font-medium">{result.message}</p>
                {result.count && (
                  <p className="text-sm mt-1 opacity-80">
                    {result.count} records imported successfully
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Instructions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Instructions</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-slate-600">
            <li className="flex items-start gap-2">
              <span className="font-semibold text-slate-900">1.</span>
              Download the template CSV for your selected data type
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-slate-900">2.</span>
              Fill in your data following the column format
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-slate-900">3.</span>
              Save as CSV or Excel format
            </li>
            <li className="flex items-start gap-2">
              <span className="font-semibold text-slate-900">4.</span>
              Upload the file and click "Upload & Import"
            </li>
          </ul>
          <div className="mt-4 p-3 bg-amber-50 rounded-lg text-sm text-amber-800">
            <strong>Note:</strong> For GST rates, use percentages (e.g., 9 for 9%). 
            Dates should be in YYYY-MM-DD format.
          </div>
        </CardContent>
      </Card>
      </>
      )}
    </div>
  );
}
